/**
 * Route Tracking Utilities (Issue #5115)
 *
 * Provides route normalization for aggregating page view analytics
 * and route exclusion for filtering non-trackable paths.
 */

/**
 * Patterns that match dynamic ID segments in routes.
 * Order matters: more specific patterns first.
 */
const DYNAMIC_SEGMENT_PATTERNS: RegExp[] = [
  // UUID: 8-4-4-4-12 hex pattern
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // CUID/CUIDv2: starts with 'c', 20+ alphanumeric chars
  /^c[a-z0-9]{19,}$/i,
]

/** Route prefixes that should not be tracked. */
const EXCLUDED_PREFIXES = [
  '/api/',
  '/_next/',
  '/auth/',
  '/favicon',
  '/icon',
  '/apple-icon',
]

/**
 * Normalize a route path by replacing dynamic segments (IDs)
 * with `:id` for aggregation in analytics.
 *
 * Examples:
 * - `/people/cm1abc2def3ghi4jkl5mno6p` -> `/people/:id`
 * - `/meetings/550e8400-e29b-41d4-a716-446655440000/notes` -> `/meetings/:id/notes`
 * - `/today` -> `/today`
 */
export function normalizeRoute(path: string): string {
  // Remove trailing slash (except for root)
  const cleaned = path.length > 1 ? path.replace(/\/$/, '') : path

  const segments = cleaned.split('/')
  const normalized = segments.map(segment => {
    if (!segment) return segment
    for (const pattern of DYNAMIC_SEGMENT_PATTERNS) {
      if (pattern.test(segment)) {
        return ':id'
      }
    }
    return segment
  })

  return normalized.join('/')
}

/**
 * Check if a route should be excluded from automatic tracking.
 */
export function isExcludedRoute(path: string): boolean {
  return EXCLUDED_PREFIXES.some(prefix => path.startsWith(prefix))
}
